import { useState, useRef, useCallback } from 'react';

export const useInteraction = () => {
  const [isLiveMode, setIsLiveMode] = useState(false);
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [canvasScale, setCanvasScale] = useState(1);
  const [selectionBox, setSelectionBox] = useState(null);
  const [snapLines, setSnapLines] = useState([]);

  const draggingRef = useRef(null);
  const resizingRef = useRef(null);
  const panRef = useRef(null);
  const interactionData = useRef({ nodesBefore: null, selectionBefore: null });

  const startDragging = useCallback((data) => {
    draggingRef.current = data;
  }, []);

  const stopDragging = useCallback(() => {
    draggingRef.current = null;
  }, []);

  const startResizing = useCallback((data) => {
    resizingRef.current = data;
  }, []);

  const stopResizing = useCallback(() => {
    resizingRef.current = null;
  }, []);

  const startPanning = useCallback((data) => {
    panRef.current = data;
    setIsPanning(true);
  }, []);

  const stopPanning = useCallback(() => {
    panRef.current = null;
    setIsPanning(false);
  }, []);

  const startSelectionBox = useCallback((data) => {
    setSelectionBox(data);
  }, []);

  const updateSelectionBox = useCallback((updates) => {
    setSelectionBox(prev => prev ? { ...prev, ...updates } : null);
  }, []);

  const stopSelectionBox = useCallback(() => {
    setSelectionBox(null);
  }, []);

  const saveInteractionState = useCallback((nodesBefore, selectionBefore) => {
    interactionData.current = { nodesBefore, selectionBefore };
  }, []);

  const clearInteractionState = useCallback(() => {
    interactionData.current = { nodesBefore: null, selectionBefore: null };
  }, []);

  const resetInteraction = useCallback(() => {
    stopDragging();
    stopResizing();
    stopPanning();
    stopSelectionBox();
    clearInteractionState();
    setSnapLines([]);
  }, [stopDragging, stopResizing, stopPanning, stopSelectionBox, clearInteractionState]);

  return {
    // State
    isLiveMode,
    setIsLiveMode,
    isSpacePressed,
    setIsSpacePressed,
    isPanning,
    canvasScale,
    setCanvasScale,
    selectionBox,
    snapLines,
    setSnapLines,

    // Refs
    draggingRef,
    resizingRef,
    panRef,
    interactionData,

    // Actions
    startDragging,
    stopDragging,
    startResizing,
    stopResizing,
    startPanning,
    stopPanning,
    startSelectionBox,
    updateSelectionBox,
    stopSelectionBox,
    saveInteractionState,
    clearInteractionState,
    resetInteraction
  };
};
